import { MediaManagerBoard } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import supabase from 'modules/core/supabase/supabase';
import { BOARD_RESOLUTIONS, resolveBoardResolution } from 'modules/manager/domain/board-resolutions';
import { useCallback, useMemo, useState } from 'react';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import MediaManagerBoardForm, {
  DEFAULT_RESOLUTION_OPTION,
  getResolutionOption,
  MediaManagerBoardFormValues,
} from '../../components/forms/media-manager-board-form';

interface Args {
  boards: MediaManagerBoard[];
  onUpdated: () => void;
}

function useEditBoardModal({ boards, onUpdated }: Args) {
  const [board, setBoard] = useState<MediaManagerBoard | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const open = useCallback((next: MediaManagerBoard) => {
    setBoard(next);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  const existingNames = useMemo(
    () => boards.map((b) => b.name.trim()).filter((name) => name !== board?.name),
    [boards, board],
  );

  const onSubmit = useCallback(
    async (values: MediaManagerBoardFormValues) => {
      if (!board) return;
      setIsLoading(true);
      try {
        const resolution = BOARD_RESOLUTIONS[values.resolution.value];
        await supabase.mediaManager.updateBoard({
          id: board.id,
          name: values.name.trim(),
          width: resolution.width,
          height: resolution.height,
        });
        onUpdated();
        close();
      } catch (error) {
        console.error('Failed to update media manager board:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [board, onUpdated, close],
  );

  return {
    open,
    close,
    modal: (
      <Modal show={isOpen} onClose={close}>
        <UDText type="title-3" className="mb-5">
          Редактировать табло
        </UDText>
        <MediaManagerBoardForm
          initialValues={{
            name: board?.name ?? '',
            resolution: board
              ? getResolutionOption(resolveBoardResolution(board.width, board.height).key)
              : DEFAULT_RESOLUTION_OPTION,
          }}
          existingNames={existingNames}
          isLoading={isLoading}
          submitLabel="Сохранить"
          onSubmit={onSubmit}
          enableReinitialize
        />
      </Modal>
    ),
  };
}

export default useEditBoardModal;
